import React, { memo } from "react";

import { styled } from "@linaria/react";
import * as io from "io-ts";
import createIOSortType from "../../../../lib/createIOSortType";
import createGlobalState from "@ui/lib/createGlobalState";
import useFilteredDatas from "@ui/lib/hooks/useFilteredDatas";
import useModal from "@ui/lib/hooks/useModal";
import useSortedData from "../../../../lib/hooks/useSortedData";
import t from "../../../../i18n";
import type { SortableValue } from "diagnostics-lib/comparator";
import { color, fonts } from "@ui";
import SecondaryHeader from "@ui/Header/SecondaryHeader";
import Link from "@ui/Link";
import Modal from "@ui/Modal";
import SidePanelLayout from "@ui/SidePanel/Layout";
import Table from "@ui/Table";
import TableCard from "@ui/Table/Card";
import { useProject, type Project } from "../../../../api/useProjects";
import { useQuantities } from "../../../../api/useQuantities";
import DeleteFiltersModalForm from "./DeleteFiltersModalForm";
import LoadFiltersModalForm from "./LoadFiltersModalForm";
import type { FilterColumn, ParsedFilter } from "./types";
import { filterColumns, FilterProperties } from "./types";
import type { ActivationFilter } from "../../../../api/useActivationFilters";
import useActivationFilters from "../../../../api/useActivationFilters";
import useMetadata from "./useMetadata";
import { useCanUserDoOnProject } from "../../../canUserDo";
import SearchInputV2 from "@ui/Input/SearchInputV2";
import { LoadedFileName } from "../LoadedFileName";
import { TableCardWithoutBorder } from "@ui/Table/_styles/TableCardWithoutBorder";
import Button from "@ui/Button";
import { DeleteBinLine, DownloadLine, More2Fill, UploadLine } from "@ui/IconV2/react";
import Option, { OptionGroup } from "@ui/Option";
import Menu from "@ui/Menu";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { useGet } from "../../../../api/useCall";
import download from "downloadjs";
import { generateExportFileName } from "diagnostics-lib/string";
import { css } from "@linaria/core";
import { useParams } from "react-router";
import { toPath } from "../../../../router/helpers/path";

const ContainerHeaderRight = css`
  display: flex;
  gap: 8px;
`;

const StyledSidePanelLayout = styled(SidePanelLayout)`
  gap: 0px;
`;

const StyledMenu = styled(Menu)`
  font: ${fonts.body2};
`;

const computeRowKey = (filter: ActivationFilter) => `filter-id-${filter.quantityId}`;

const BreadcrumbWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

const StyledLink = styled(Link)`
  color: ${color.lightNitrogen500_Main};
  font: ${fonts.body1};
  font-weight: 500;
  :hover {
    text-decoration: underline;
  }
`;

const StyledTableContainer = styled.div`
  .EmptyCard {
    flex: none;
  }
`;

const Breadcrumb = ({ project }: P) => (
  <BreadcrumbWrapper>
    <StyledLink to={toPath("/projects/:projectId/settings", { projectId: project.id })}>
      {t("project.secondary_header.settings.title")}
    </StyledLink>
    <div>{">"}</div>
    <div>{t("project.secondary_header.filters.title")}</div>
  </BreadcrumbWrapper>
);

const useSort = createGlobalState({
  storage: "session",
  key: "filterSort",
  type: createIOSortType(filterColumns),
  defaultValue: {
    column: FilterProperties.QUANTITY,
    direction: "asc",
  },
  scoped: true,
});

const sortSelector: { [key in FilterColumn]: (filter: ParsedFilter) => SortableValue | null } = {
  [FilterProperties.MAXIMUM_STD]: filter => filter.maxStd,
  [FilterProperties.MINIMUN_VALUE]: filter => filter.min,
  [FilterProperties.QUANTITY]: filter => filter.name,
  [FilterProperties.MAXIMUM_VALUE]: filter => filter.max,
};

const useFiltersSearch = createGlobalState({
  storage: "session",
  key: "filtersSearch",
  type: io.string,
  defaultValue: "",
  scoped: true,
});

interface P {
  project: Project;
}

export const Filters = memo(() => {
  const { projectId } = useParams();
  if (!projectId) throw new Error("");
  const project = useProject(projectId);
  if (!project) throw new Error("");

  const canEditSetup = useCanUserDoOnProject("PROJECT_SETUP", project.id);
  const {
    modal: isLoadFiltersModalOpen,
    openModal: openLoadFiltersModal,
    closeModal: closeLoadFiltersModal,
  } = useModal();
  const {
    modal: isDeleteFiltersModalOpen,
    openModal: openDeleteFiltersModal,
    closeModal: closeDeleteFiltersModal,
  } = useModal();
  const [sort, setSort] = useSort({ scope: project.id });
  const [search, setSearch] = useFiltersSearch({ scope: project.id });
  const filters = useActivationFilters(project.id);
  const quantities = useQuantities(project.id);
  const exportActivationFilters = useGet(
    "/projects/:projectId(uuid)/quantities/activationFilters/export",
  );

  const handleExport = React.useCallback(async () => {
    const res = await exportActivationFilters({ routeParams: { projectId: project.id } });

    download(
      res.body as unknown as Blob,
      generateExportFileName({
        filename: "activation_filters",
        projectName: project.name,
        extension: "csv",
      }),
    );
  }, [exportActivationFilters, project.name, project.id]);

  const parsedFilters: ParsedFilter[] = React.useMemo(
    () =>
      filters.map(filter => {
        const quantity = quantities.find(quantity => quantity.id === filter.quantityId);
        if (quantity) {
          return {
            ...filter,
            name: quantity.name,
            unit: quantity.unit,
          };
        }

        return { ...filter, unit: null, name: null };
      }),
    [filters, quantities],
  );
  const metadata = useMetadata();
  const [searchedFilters, searchInProgress] = useFilteredDatas(
    parsedFilters,
    search,
    d => d.name || "",
  );
  const sortedFilters = useSortedData(searchedFilters, sortSelector[sort.column], sort.direction);

  const handleSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value);
    },
    [setSearch],
  );

  const headerLeft = (
    <SearchInputV2
      data-cy="search-filter"
      placeholder={t("commons.placeholder.search")}
      onChange={handleSearchChange}
      value={search}
    />
  );

  const headerRight = (
    <div className={ContainerHeaderRight}>
      {canEditSetup ? (
        <Button variant="primary" onClick={() => openLoadFiltersModal()} leftIcon={<UploadLine />}>
          {t("commons.message_card.replace")}
        </Button>
      ) : null}

      {canEditSetup ? (
        <StyledMenu
          arrowWidth={0}
          //   gap={16}
          placement="bottom-end"
          overlay={
            <>
              <OptionGroup>
                <Option closeOnClick onClick={handleExport} disabled={!canEditSetup}>
                  <DownloadLine color={color.carbon800} />
                  {t("commons.message_card.export")}
                </Option>
                <Option
                  closeOnClick
                  onClick={() => openDeleteFiltersModal()}
                  disabled={!canEditSetup}
                >
                  <DeleteBinLine color={color.carbon800} />
                  {t("commons.message_card.delete")}
                </Option>
              </OptionGroup>
            </>
          }
        >
          <ButtonIconV2>
            <More2Fill size="m" />
          </ButtonIconV2>
        </StyledMenu>
      ) : null}
    </div>
  );

  return (
    <>
      <SecondaryHeader breadcrumb={<Breadcrumb project={project} />} />

      <StyledSidePanelLayout id="quantities" title="">
        <LoadedFileName fileName={project.activationFiltersFileName} />
        <StyledTableContainer>
          <TableCard
            title=""
            headerLeft={headerLeft}
            headerRight={headerRight}
            className={TableCardWithoutBorder}
          >
            <Table
              stickyHeader
              loading={searchInProgress}
              datas={sortedFilters}
              metadata={metadata}
              computeRowKey={computeRowKey}
              sort={sort}
              onSort={setSort}
            />
          </TableCard>
        </StyledTableContainer>
      </StyledSidePanelLayout>

      <Modal
        title={t("project.settings.analyses.add_filters.modal.title")}
        isOpen={isLoadFiltersModalOpen}
        onRequestClose={closeLoadFiltersModal}
      >
        <LoadFiltersModalForm
          onLoadFilterClose={closeLoadFiltersModal}
          closeLoadFiltersModal={closeLoadFiltersModal}
          projectId={project.id}
          isEdit
        />
      </Modal>

      <Modal
        title={t("project.settings.analyses.delete_filters.modal.title")}
        isOpen={isDeleteFiltersModalOpen}
        onRequestClose={closeDeleteFiltersModal}
      >
        <DeleteFiltersModalForm
          closeDeleteFiltersModal={closeDeleteFiltersModal}
          projectId={project.id}
        />
      </Modal>
    </>
  );
});
